import axios, { type AxiosRequestConfig } from 'axios'
import { Mutex } from 'async-mutex'
import router from '@/router/observerRoutes'
import { useProfessionalSessionStore } from '@/stores/ProfessionalSessionStore'
import {
  mapThirdPartyAccessErrorCodeToRestrictedReason,
  type ThirdPartyAccessErrorResponse
} from '@/types/observer.types'
import { registerMockInterceptor } from './observerMockInterceptor'

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Opt out of the 404/403 "restricted" redirect for background lookups and endpoints that may not exist yet */
    skipRestrictedRedirect?: boolean
    _retried?: boolean
  }
}

type RetryableRequestConfig = AxiosRequestConfig & { _retried?: boolean }

type RequestHeaders = Record<string, string | undefined> & {
  // eslint-disable-next-line no-unused-vars
  get?: (...args: [string]) => string | undefined
}

type QueuedRequest = {
  // eslint-disable-next-line no-unused-vars
  resolve: (...args: [string]) => void
  // eslint-disable-next-line no-unused-vars
  reject: (...args: [unknown]) => void
}

let requestQueue: QueuedRequest[] = []
const refreshMutex = new Mutex()

function getAuthorizationHeader(
  config: RetryableRequestConfig
): string | undefined {
  const headers = config.headers as RequestHeaders | undefined

  if (!headers) {
    return undefined
  }

  if (typeof headers.get === 'function') {
    return headers.get('Authorization')
  }

  return headers.Authorization ?? headers.authorization
}

function setAuthorizationHeader(
  config: RetryableRequestConfig,
  token: string
): void {
  config.headers ??= {}
  const headers = config.headers as RequestHeaders
  headers.Authorization = `Bearer ${token}`
}

function queueRequest(config: RetryableRequestConfig) {
  return new Promise<string>((resolve, reject) => {
    requestQueue.push({ resolve, reject })
  }).then((token) => {
    setAuthorizationHeader(config, token)
    return professionalClient(config)
  })
}

function processQueue(error: unknown, token: string | null = null): void {
  requestQueue.forEach(({ resolve, reject }) => {
    if (error || token === null) {
      reject(error ?? new Error('Missing refreshed observer token'))
      return
    }

    resolve(token)
  })

  requestQueue = []
}

function endExpiredSessionOnce(
  sessionStore: ReturnType<typeof useProfessionalSessionStore>
): void {
  if (sessionStore.sessionStatus === 'ended') {
    return
  }

  const currentPath = router.currentRoute.value.fullPath

  sessionStore.endSession()
  router.push({
    name: 'observer-session-ended',
    query: {
      reason: 'expired',
      returnTo: currentPath !== '/session-ended' ? currentPath : undefined
    }
  })
}

async function retryAfterRefresh(
  originalRequest: RetryableRequestConfig,
  error: unknown
) {
  const sessionStore = useProfessionalSessionStore()
  const currentToken = sessionStore.professionalToken

  if (
    currentToken &&
    getAuthorizationHeader(originalRequest) !== `Bearer ${currentToken}`
  ) {
    setAuthorizationHeader(originalRequest, currentToken)
    return professionalClient(originalRequest)
  }

  if (refreshMutex.isLocked()) {
    return queueRequest(originalRequest)
  }

  const release = await refreshMutex.acquire()

  try {
    const latestToken = sessionStore.professionalToken

    if (
      latestToken &&
      getAuthorizationHeader(originalRequest) !== `Bearer ${latestToken}`
    ) {
      // Flush any requests that queued while this holder awaited the mutex,
      // otherwise their promises never settle on this early-return path
      processQueue(null, latestToken)
      setAuthorizationHeader(originalRequest, latestToken)
      return professionalClient(originalRequest)
    }

    const refreshed = await sessionStore.refreshToken()
    const refreshedToken = sessionStore.professionalToken

    if (refreshed && refreshedToken) {
      processQueue(null, refreshedToken)
      setAuthorizationHeader(originalRequest, refreshedToken)
      return professionalClient(originalRequest)
    }

    processQueue(error)
    endExpiredSessionOnce(sessionStore)
    return Promise.reject(error)
  } catch (refreshError) {
    processQueue(refreshError)
    endExpiredSessionOnce(sessionStore)
    return Promise.reject(refreshError)
  } finally {
    release()
  }
}

/**
 * Separate axios instance for professional observer API calls.
 * Never touches parent token infrastructure (LoginStore, CommonStore, etc.).
 */
const professionalClient = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL,
  timeout: 240000,
  headers: {
    'Content-Type': 'application/json',
    AID: 'Observer',
    'App-Version': import.meta.env.VITE_APP_VERSION
  }
})

professionalClient.interceptors.request.use((config) => {
  const sessionStore = useProfessionalSessionStore()

  if (sessionStore.professionalToken) {
    setAuthorizationHeader(config, sessionStore.professionalToken)
  }

  return config
})

professionalClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject('Canceled')
    }

    const status = error?.response?.status
    const originalRequest = error?.config as RetryableRequestConfig | undefined

    // Attempt silent refresh on 401 (once per request)
    if (status === 401 && originalRequest && !originalRequest._retried) {
      originalRequest._retried = true
      return retryAfterRefresh(originalRequest, error)
    }

    if (status === 403) {
      if (!originalRequest?.skipRestrictedRedirect) {
        const errorBody = error?.response?.data as
          ThirdPartyAccessErrorResponse | undefined
        const reason = mapThirdPartyAccessErrorCodeToRestrictedReason(
          errorBody?.errorCode
        )
        router.push({ name: 'observer-restricted', query: { reason } })
      }
    } else if (status === 404) {
      // 404 from professional endpoints = client/connection not found
      if (!originalRequest?.skipRestrictedRedirect) {
        router.push({
          name: 'observer-restricted',
          query: { reason: 'NO_ACTIVE_MATCH' }
        })
      }
    }

    return Promise.reject(error)
  }
)

export { professionalClient }

registerMockInterceptor(professionalClient)
